import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Approach } from "./approach";
import Hero from "./hero";

const Container = styled.div`
  max-width: 100%; // 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;
  font-family: "Manrope", sans-serif;
  box-sizing: border-box;
`;

const Section = styled(motion.section)`
  margin-bottom: 5rem;
`;

const HeroSection = styled(Section)`
  text-align: center;
`;

const ContentWrapper = styled(motion.section)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4rem;
  margin-bottom: 5rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
const Content = styled.div`
  text-align: left;
`;

const Image = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 40px;
  color: #102a8e;
  margin-bottom: 20px;
  font-weight: bold;
`;

const Subtitle = styled.p`
  color: #666;
  max-width: 750px;
  line-height: 1.6;
  font-size: 16px;
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  align-items: center;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 4rem;
  }
`;

interface ContentColProps {
  $order?: string;
}

const ContentCol = styled.div<ContentColProps>`
  flex: 1;
  order: ${(props) => props.$order || "unset"};

  @media (min-width: 768px) {
    order: unset;
  }
`;

const SectionLabel = styled.div`
  color: #5575f7;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #eff2ff;
  padding: 10px;
  width: 330px;
  border-radius: 10px;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: #102a8e;
  margin-bottom: 1rem;
  font-weight: bold;
`;

const Text = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 250px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const Logo = styled.img`
  width: 100%;
  height: auto;
`;

export const About = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  return (<div style={{width: "100%"}}>
        <Hero
          title="Empowering You to Stand Out in Your Career Journey"
          subtitle="Whether you're a job seeker, career changer, or professional aiming higher, Compelling Candidature helps you craft persuasive applications, ace interviews, and secure roles that match your true potential."
          backgroundImage={"/assets/hero.png"}
        />
      <Container>
        <HeroSection>
          <ContentWrapper initial="hidden" animate="visible" variants={fadeIn}>
            <Content>
              <Title>About Us</Title>
              <Subtitle>
                <p>Are you a job seeker eager to stand out in today’s competitive market? Or a professional navigating a career transition and looking for the right guidance? Compelling Candidature is here to walk with you every step of the way.</p>

                <p>We provide expert support, practical tools, and AI-powered resources to help you craft persuasive personal statements, prepare confidently for interviews, and land roles that truly reflect your potential.</p>

                <p>Whether you’re a recent graduate, an international applicant, or an experienced professional seeking growth, our tailored approach ensures you don’t just apply for opportunities — you own your narrative and present your best self to the world.</p>
              </Subtitle>
            </Content>
            <LogoWrapper>
              <Logo src="/assets/logo.png" alt="MedicsKe Logo" />
            </LogoWrapper>
          </ContentWrapper>
        </HeroSection>

        <Section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <FlexContainer>
            <ContentCol>
              <Image
                src="/assets/about1.png"
                alt="Healthcare professional"
                variants={fadeIn}
              />
            </ContentCol>
            <ContentCol>
              <SectionLabel>Take your career to the next level</SectionLabel>
              <SectionTitle>Our mission is clear</SectionTitle>
              <Text>
                We exist to help people move forward in their careers with clarity, confidence, and direction.
                <br/>
                Compelling Candidature focuses on the moments that matter most: how you tell your story, how you prepare for interviews, and how you position yourself for the roles you want. 
                Through practical tools, guided support, and targeted resources, we help you approach opportunities with intention rather than uncertainty.
              </Text>
              <Text>
                Whether you are stepping into the UK workforce, returning after a break, or aiming for progression, our role is simple; to walk alongside you as you translate your experience into impact and secure roles that truly fit your potential.
              </Text>
            </ContentCol>
          </FlexContainer>
        </Section>

        <Section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <FlexContainer>
            <ContentCol $order="2">
              <SectionLabel>Take your career to the next level</SectionLabel>
              <SectionTitle>
                The best opportunities are what you need
              </SectionTitle>
              <Text>
                We understand that every healthcare professional's journey is
                unique. That's why we offer personalized learning paths, adaptive
                assessments, and targeted resources to help you focus on what
                matters most for your career advancement.
              </Text>
              <Text>
                Our platform combines cutting-edge technology with expert content
                to create an engaging and effective learning experience that fits
                your schedule and goals.
              </Text>
            </ContentCol>
            <ContentCol $order="1">
              <Image
                src="/assets/about2.png"
                alt="Healthcare opportunities"
                variants={fadeIn}
              />
            </ContentCol>
          </FlexContainer>
        </Section>
        <Approach />
      </Container>
    </div>);
};
